import { init, track as amplitudeTrack, Identify, identify, reset, setUserId } from '@amplitude/analytics-browser';
import type { EnhancedStore } from '@reduxjs/toolkit';
import type { History, Location } from 'history';
import { v4 as uuid } from 'uuid';
import { appConfig } from 'src/config';
import type { AppState } from 'src/reducers';
import type { AccountProfile } from 'src/types/accountProfile';
import { Channel } from 'src/reducers/slices/channels';

export const ANALYTICS_EVENTS = {
  PageView: 'Platform Page Viewed',
  CampaignCreationStarted: 'Campaign Creation Started',
  ChannelAndAudienceSelected: 'Channel and Audience Selected',
  CampaignPricingWarningShown: 'Campaign Pricing Warning Shown',
  PricingWarningContactClicked: 'Pricing Warning Contact Clicked',
  PrimaryChannelContentComposed: 'Primary Channel Content Composed',
  SecondaryContentComposed: 'Secondary Content Composed',
  CampaignCreationCompleted: 'Campaign Creation Completed',
  CampaignCreationClosed: 'Campaign Creation Closed',
  CampaignCreationModalBlur: 'Campaign Creation Modal Blur',
  CampaignStepValidationFailed: 'Campaign Step Validation Failed',
  SignInCompleted: 'Sign in Completed',
  LogOutCompleted: 'Log out Completed',
  TemplateCreationStarted: 'WhatsApp Template Creation Started',
  TemplateRegistered: 'WhatsApp Template Registered',
  TemplateDeleted: 'WhatsApp Template Deleted',
  TemplateViewed: 'WhatsApp Template Viewed',
  TemplateCreationClosed: 'WhatsApp Template Creation Closed',
  TemplateStatusChanged: 'WhatsApp Template Status Changed',
  TemplateCreationFailed: 'WhatsApp Template Creation Failed',
  TemplateEdited: 'WhatsApp Template Edited',
  GenAiCreationStarted: 'AI Assistant Creation Started',
  GenAiGenerated: 'AI Assistant Generated',
  GenAiSelected: 'AI Assistant Selected',
  GenAiCreationClosed: 'AI Assistant Creation Closed',
  CampaignPaused: 'Campaign Paused',
  CampaignPauseFailed: 'Campaign Pause Failed',
  CampaignResumed: 'Campaign Resumed',
  CampaignResumeFailed: 'Campaign Resume Failed',
  CampaignInfoViewed: 'Campaign Info Viewed',
  CampaignStatisticViewed: 'Campaign Statistic Viewed',
  CampaignLogsViewed: 'Campaign Logs Viewed',
  MessageSent: 'Message Sent',
  MessageSendConfirmed: 'Message Send Confirmed',
  MessageDelivered: 'Message Delivered',
  MessageFailed: 'Message Failed',
  MessageReceived: 'Message Received',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

type CampaignSession = {
  tempCampaignId: string;
  startedAt: string;
};

type PendingEvent = {
  name: AnalyticsEventName;
  props?: Record<string, unknown>;
};

/**
 * Paths under these prefixes are considered admin panel.
 * When AMPLITUDE_TRACK_ADMIN is false (default), events from these routes are not sent to Amplitude.
 * To re-enable admin tracking, set REACT_APP_AMPLITUDE_TRACK_ADMIN=true in env.
 */
const ADMIN_PATH_PREFIXES = ['/administration', '/admin'] as const;

class AnalyticsService {
  private readonly enabled = Boolean(appConfig.AMPLITUDE_API_KEY);
  private readonly trackAdmin = Boolean(appConfig.AMPLITUDE_TRACK_ADMIN);
  private readonly debugLogging = process.env.NODE_ENV !== 'production';
  private initialized = false;
  private unsubscribeStore?: () => void;
  private currentUserName?: string;
  private currentPath = '';
  private previousPageUrl?: string;
  private campaignSession?: CampaignSession;
  private lastIdentifiedUserProps?: Record<string, string | number | boolean | string[]>;
  private pendingEvents: PendingEvent[] = [];
  private lastState?: AppState;
  private resetTimeout?: ReturnType<typeof setTimeout>;

  private isAdminPath(path: string): boolean {
    return ADMIN_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
  }

  private shouldSkipTracking(): boolean {
    return this.isAdminPath(this.currentPath) && !this.trackAdmin;
  }

  initialize(store: EnhancedStore<AppState>, history: History) {
    if (!this.enabled) {
      return;
    }

    this.lastState = store.getState();

    if (!this.initialized) {
      init(appConfig.AMPLITUDE_API_KEY as string, undefined, {
        serverZone: 'EU',
        autocapture: false,
        defaultTracking: {
          pageViews: false,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
          attribution: false,
        },
      });
      this.initialized = true;

      this.currentPath = history.location.pathname;
      this.trackPageView(history.location);
      history.listen((location) => {
        this.currentPath = location.pathname;
        this.trackPageView(location);
      });
      this.flushPendingEvents();
    }

    if (!this.unsubscribeStore) {
      this.unsubscribeStore = store.subscribe(() => {
        const state = store.getState();
        this.handleStateChange(state);
      });

      this.handleStateChange(store.getState());
    }
  }

  startCampaignSession(): CampaignSession | undefined {
    if (!this.enabled) {
      return undefined;
    }

    const session: CampaignSession = {
      tempCampaignId: uuid(),
      startedAt: new Date().toISOString(),
    };

    this.campaignSession = session;
    return session;
  }

  getCampaignSession(): CampaignSession | undefined {
    return this.campaignSession;
  }

  clearCampaignSession() {
    this.campaignSession = undefined;
  }

  track(eventName: AnalyticsEventName, eventProperties?: Record<string, unknown>) {
    if (!this.enabled) {
      return;
    }

    const sanitizedProps = this.sanitizeEventProperties(eventProperties);

    if (!this.initialized) {
      this.pendingEvents.push({ name: eventName, props: sanitizedProps });
      this.logEvent(eventName, sanitizedProps, true);
      return;
    }

    if (this.shouldSkipTracking()) {
      return;
    }

    this.logEvent(eventName, sanitizedProps, false);
    amplitudeTrack(eventName, sanitizedProps);
  }

  private trackPageView(location: Location<unknown>) {
    if (!this.initialized) {
      return;
    }

    this.currentPath = location.pathname;
    if (this.shouldSkipTracking()) {
      return;
    }

    const { pathname, search } = location;
    const pageUrl = `${window.location.origin}${pathname}${search}`;
    const referrer = this.previousPageUrl ?? (document.referrer || undefined);
    const pageTitle = this.resolvePageTitle(pathname);

    this.track(ANALYTICS_EVENTS.PageView, {
      page_name: pageTitle,
      page_title: pageTitle,
      page_path: pathname,
      page_url: pageUrl,
      referrer_page: referrer,
    });

    this.previousPageUrl = pageUrl;
  }

  private handleStateChange(state: AppState) {
    this.lastState = state;

    if (!this.initialized) {
      return;
    }

    const loginState = state.login;

    if (loginState.authenticated && loginState.userData?.userName) {
      if (this.resetTimeout) {
        clearTimeout(this.resetTimeout);
        this.resetTimeout = undefined;
      }

      if (this.currentUserName !== loginState.userData.userName) {
        setUserId(loginState.userData.userName);
        this.currentUserName = loginState.userData.userName;
      }

      this.setUserProperties(loginState.userData, state);
      return;
    }

    if (!loginState.authenticated && this.currentUserName) {
      if (this.resetTimeout) {
        return;
      }

      this.resetTimeout = setTimeout(() => {
        reset();
        this.currentUserName = undefined;
        this.lastIdentifiedUserProps = undefined;
        this.pendingEvents = [];
        this.resetTimeout = undefined;
      }, 500);
    }
  }

  private setUserProperties(profile: AccountProfile, state: AppState) {
    const nextProps: Record<string, string | number | boolean | string[]> = {};
    const setIfPresent = (key: string, value?: string | number | boolean | string[] | null) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      nextProps[key] = Array.isArray(value) ? [...value] : value;
    };

    const hasWhatsappAccess =
      profile.hasWB ?? state.login.userWhatsappConfig?.ACTIVE ?? Boolean(state.meta?.configs?.length);
    const hasViberAccess = profile.hasVB ?? state.login.userViberConfig?.ACTIVE ?? false;
    const availableChannels: string[] = ['sms'];
    if (hasWhatsappAccess) {
      availableChannels.push('whatsapp');
    }
    if (hasViberAccess) {
      availableChannels.push('viber');
    }

    const roles =
      profile.authorizations && typeof profile.authorizations === 'object'
        ? Object.entries(profile.authorizations)
            .filter(([, value]) => Boolean(value))
            .map(([role]) => role)
        : [];

    setIfPresent('customer_id', profile.customerId);
    setIfPresent('customer_uuid', profile.customerUuid);
    setIfPresent('customer_name', profile.customerName);
    setIfPresent('customer_country', profile.customerCountry);
    setIfPresent('customer_currency', profile.customerCurrency);
    // Use a single timezone property to avoid duplicates in Amplitude
    setIfPresent('default_time_zone', profile.defaultTimeZone);
    setIfPresent('language', state.locale.lang);
    setIfPresent('user_role', roles.length ? roles.join(',') : null);
    setIfPresent('available_channels', availableChannels);
    setIfPresent('has_whatsapp_access', hasWhatsappAccess ?? null);
    setIfPresent('has_viber_access', hasViberAccess ?? null);
    setIfPresent('chat_agent_enabled', profile.chatAgent);

    if (this.areUserPropertiesEqual(nextProps)) {
      return;
    }

    const identifyObj = new Identify();
    Object.entries(nextProps).forEach(([key, value]) => {
      identifyObj.set(key, value as string | number | boolean | string[]);
    });

    identify(identifyObj);
    this.logEvent('$identify', nextProps);
    this.lastIdentifiedUserProps = nextProps;
  }

  private areUserPropertiesEqual(nextProps: Record<string, string | number | boolean | string[]>): boolean {
    if (!this.lastIdentifiedUserProps) {
      return false;
    }

    const prevEntries = Object.entries(this.lastIdentifiedUserProps);
    const nextEntries = Object.entries(nextProps);

    if (prevEntries.length !== nextEntries.length) {
      return false;
    }

    return nextEntries.every(([key, value]) => {
      const previousValue = this.lastIdentifiedUserProps![key];

      if (Array.isArray(previousValue) && Array.isArray(value)) {
        return previousValue.length === value.length && previousValue.every((entry, index) => entry === value[index]);
      }

      return previousValue === value;
    });
  }

  private sanitizeEventProperties(eventProperties?: Record<string, unknown>) {
    if (!eventProperties) {
      return undefined;
    }

    return Object.entries(eventProperties).reduce<Record<string, unknown>>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }

      return acc;
    }, {});
  }

  private flushPendingEvents() {
    if (!this.pendingEvents.length || !this.initialized) {
      return;
    }
    if (this.shouldSkipTracking()) {
      this.pendingEvents = [];
      return;
    }

    this.pendingEvents.forEach(({ name, props }) => {
      this.logEvent(name, props, false);
      amplitudeTrack(name, props);
    });

    this.pendingEvents = [];
  }

  private logEvent(_name: string, _props?: Record<string, unknown>, _buffered = false) {
    if (!this.debugLogging) {
      return;
    }
  }

  private resolvePageTitle(pathname: string) {
    const normalizedPath = pathname.replace(/\/+$/, '') || '/';
    const templatesTitle = () => {
      const channel = this.lastState?.channels?.channel;
      if (channel === Channel.whatsapp) {
        return 'Templates - WhatsApp';
      }
      if (channel === Channel.viber) {
        return 'Templates - Viber';
      }
      return 'Templates';
    };

    const pageTitles: Record<string, string | (() => string)> = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/smsCampaign/history': 'Campaigns',
      '/channels': templatesTitle,
      '/smschat': 'Inbox',
      '/contacts/manage': 'Audiences',
      '/finance/pricing': 'Pricing',
      '/finance/balance': 'Analytics Overview',
      '/finance/cdrs': 'Analytics CDRs',
      '/account/profile': 'Account',
    };

    const mappedTitle = pageTitles[normalizedPath];
    if (mappedTitle) {
      return typeof mappedTitle === 'function' ? mappedTitle() : mappedTitle;
    }

    const segments = normalizedPath.split('/').filter(Boolean);
    if (!segments.length) {
      return 'Dashboard';
    }

    const lastSegment = segments[segments.length - 1].replace(/[-_]/g, ' ');
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  }
}

export const analyticsService = new AnalyticsService();

export const initializeAnalytics = (store: EnhancedStore<AppState>, history: History) => {
  analyticsService.initialize(store, history);
};
